// boxDesign.js – Schachteldeckel (aufgeklappt) als SVG für den Druck
// Nachgebaut nach der Vorlage von BandeleArt (violett / petrol / creme).
// Koordinaten: viewBox 0 0 2000 1610 (gleiches Seitenverhältnis wie die Vorlage).
//   Mitte (creme)       = Deckel-Oberseite: Logo, „für kleine Familienforscher“, Titel
//   oben (violett)      = Seitenwand, Text steht auf dem Kopf
//   links (petrol)      = Seitenwand, Text von oben nach unten
//   rechts (violett)    = Seitenwand, Text von unten nach oben
//   unten (petrol)      = Seitenwand mit CE-Zeichen und Herstellerangaben
import { LOGO_VIEWBOX, logoMarkup } from './logoSvg';
import { scSvg } from './smallCaps';

const LEFT2 = 'Individuelle Stammbaum MeMos auf Anfrage';
const TOP1 = 'Stammbaum MeMo für kleine Familienforscher';
export const BOX_W = 2000;
export const BOX_H = 1610;
const PURPLE = '#662e93';
const TEAL = '#316f7e';
const CREAM = '#f9f1e6';
const BROWN = '#42210b';
const FONT = "'Bandeleart', 'MemoHand', cursive";

// Grenzen der geschwungenen Flächen (aus der Vorlage vermessen), von oben nach unten
const LEFT_CURVE = [[272, 605], [312, 650], [347, 700], [373, 750], [395, 800], [416, 850], [439, 900], [463, 950],
  [485, 990], [505, 1025], [529, 1050], [552, 1075], [580, 1100], [617, 1125], [680, 1152], [737, 1175], [775, 1200],
  [803, 1225], [825, 1250], [844, 1275], [858, 1296]];
const RIGHT_CURVE = [[1310, 312], [1356, 337], [1389, 362], [1418, 387], [1443, 412], [1466, 437], [1486, 462],
  [1503, 487], [1519, 512], [1538, 537], [1553, 562], [1575, 587], [1596, 612], [1610, 637], [1621, 662], [1635, 687],
  [1648, 712], [1672, 737], [1695, 762], [1723, 787], [1728, 790]];

// Punkte als weiche Kurve (Catmull-Rom → Bézier). Ohne „M“ am Anfang.
function smooth(pts) {
  let d = '';
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0].toFixed(1)} ${c1[1].toFixed(1)}, ${c2[0].toFixed(1)} ${c2[1].toFixed(1)}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

// Violette Fläche (oben + rechts) und creme Fläche (Mitte); der Rest ist petrol.
const PURPLE_PATH = `M 0 0 L ${BOX_W} 0 L ${BOX_W} 1296 L 1728 1296 L 1728 790`
  + smooth([...RIGHT_CURVE].reverse()) + ' L 0 312 Z';
const CREAM_PATH = 'M 272 312 L 1310 312' + smooth(RIGHT_CURVE) + ' L 1728 1296 L 858 1296'
  + smooth([...LEFT_CURVE].reverse()) + ' Z';

// Stammbaum-Symbole: Quadrate (x, y oben links, 70 × 70) und Verbindungslinien
const sq = (x, y) => `M ${x} ${y} h 70 v 70 h -70 Z`;
const ln = (x1, y1, x2, y2) => `M ${x1} ${y1} L ${x2} ${y2}`;
const TREE_RIGHT = [sq(1557, 400), sq(1503, 512), sq(1611, 512), sq(1447, 627), sq(1557, 627), sq(1638, 808),
  ln(1592, 470, 1592, 488), ln(1538, 488, 1646, 488), ln(1538, 488, 1538, 512), ln(1646, 488, 1646, 512),
  ln(1538, 582, 1538, 600), ln(1482, 600, 1592, 600), ln(1482, 600, 1482, 627), ln(1592, 600, 1592, 627),
  ln(1646, 582, 1646, 600), ln(1646, 600, 1673, 600), ln(1673, 600, 1673, 808)].join(' ');
const TREE_LEFT = [sq(325, 963), sq(453, 963), sq(390, 1067), sq(280, 1183), sq(390, 1183), sq(502, 1183),
  ln(395, 998, 453, 998), ln(425, 998, 425, 1067), ln(425, 1137, 425, 1157), ln(315, 1157, 537, 1157),
  ln(315, 1157, 315, 1183), ln(425, 1157, 425, 1183), ln(537, 1157, 537, 1183), ln(425, 1253, 425, 1345)].join(' ');

// CE-Zeichen (zwei Halbkreise und Mittelstrich)
const CE = 'M 778 1373 A 26 26 0 1 0 778 1425 M 806 1373 A 26 26 0 1 0 806 1425 M 783 1399 L 806 1399';

function esc(s) {
  return String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

// Schriftgrösse so verkleinern, dass der Text höchstens maxW breit ist.
// measure(text, size) misst die Breite (im Browser mit der echten Schrift); ohne measure: Schätzung.
function fit(text, size, maxW, measure) {
  const w = measure ? measure(text, size) : String(text).length * size * 0.6;
  return w > maxW ? Math.floor(size * maxW / w) : size;
}

/**
 * opts:
 *   title       – Name auf der Schachtel (z.B. „Familie Muster“)
 *   extension   – true = Erweiterung (Etikett „Erweiterung“ und Zusatz oben)
 *   families    – Farben der Familienstämme (bestimmt Logo-Variante und Kartenfarben)
 *   cards       – Anzahl Karten in der Schachtel
 *   artNr       – Artikel-/Losnummer
 *   fontDataUrl – Schrift „Bandeleart“ als data:-Adresse (wird ins SVG eingebettet)
 *   measure     – optional: Funktion (text, size) → Breite in Kapitälchen, für das Einpassen langer Texte
 * Alle Texte ausser den Herstellerangaben stehen in Kapitälchen (siehe smallCaps.js).
 */
export function buildBoxSvg({ title = '', extension = false, families = [], cards = 72, artNr = '', fontDataUrl = '', measure } = {}) {
  const n = families.length;
  const logo = (x, y, w) => {
    const h = w * 502 / 1040;
    return `<svg x="${x}" y="${y}" width="${w}" height="${h.toFixed(1)}" viewBox="${LOGO_VIEWBOX}">${logoMarkup(n, families)}</svg>`;
  };
  const fam = `${n || 1} ${n === 1 ? 'Familienstamm' : 'Familienstämme'}`;
  const topLine2 = `${title}${extension ? ', Erweiterung' : ''}`;
  const titleSize = fit(title, 72, 900, measure);
  const top1Size = fit(TOP1, 61, 1400, measure);
  const top2Size = fit(topLine2, 61, 1400, measure);
  const leftSize = fit(LEFT2, 42, 900, measure);
  const sc = (text, size) => scSvg(text, size, esc);   // Text in Kapitälchen
  const fontFace = fontDataUrl
    ? `@font-face{font-family:'Bandeleart';src:url(${fontDataUrl}) format('woff2');}` : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BOX_W} ${BOX_H}" width="${BOX_W}" height="${BOX_H}">
<defs>
<style>${fontFace}
text{font-family:${FONT};}
</style>
<clipPath id="creme"><path d="${CREAM_PATH}"/></clipPath>
</defs>
<rect width="${BOX_W}" height="${BOX_H}" fill="${TEAL}"/>
<path d="${PURPLE_PATH}" fill="${PURPLE}"/>
<path d="${CREAM_PATH}" fill="${CREAM}"/>
<!-- Stammbaum-Symbole: auf den farbigen Flächen creme, auf der creme Fläche farbig -->
<g fill="none" stroke-width="7" stroke-linejoin="round">
  <path d="${TREE_RIGHT}" stroke="${CREAM}"/>
  <path d="${TREE_RIGHT}" stroke="${PURPLE}" clip-path="url(#creme)"/>
  <path d="${TREE_LEFT}" stroke="${CREAM}"/>
  <path d="${TREE_LEFT}" stroke="${TEAL}" clip-path="url(#creme)"/>
</g>
<!-- Deckel-Oberseite -->
${logo(535, 411, 900)}
<text x="997" y="858" text-anchor="middle" font-size="80" fill="${PURPLE}">${sc('für kleine', 80)}</text>
<text x="997" y="958" text-anchor="middle" font-size="88" fill="${PURPLE}">${sc('Familienforscher', 88)}</text>
<text x="997" y="1052" text-anchor="middle" font-size="${titleSize}" fill="${BROWN}">${sc(title, titleSize)}</text>
${extension ? `<rect x="822" y="1130" width="373" height="78" rx="39" fill="${PURPLE}" stroke="${CREAM}" stroke-width="5"/>
<text x="1008" y="1185" text-anchor="middle" font-size="70" fill="${CREAM}">${sc('Erweiterung', 70)}</text>` : ''}
<!-- Seitenwand oben (auf dem Kopf) -->
<g transform="rotate(180 1000 205)" fill="${CREAM}" text-anchor="middle">
  <text x="1000" y="171" font-size="${top1Size}">${sc(TOP1, top1Size)}</text>
  <text x="1000" y="246" font-size="${top2Size}">${sc(topLine2, top2Size)}</text>
</g>
<!-- Seitenwand links -->
<g transform="translate(180 805) rotate(90)" fill="${CREAM}" text-anchor="middle">
  <text x="0" y="0" font-size="64">${sc('www.BandeleArt.com', 64)}</text>
  <text x="0" y="58" font-size="${leftSize}">${sc(LEFT2, leftSize)}</text>
</g>
<!-- Seitenwand rechts -->
<g transform="translate(1822 585) rotate(-90)" fill="${CREAM}" text-anchor="middle">
  <text x="0" y="0" font-size="43">${sc('Mit zusätzlichen', 43)}</text>
  <text x="0" y="52" font-size="43">${sc('Spielideen', 43)}</text>
</g>
<g transform="translate(1822 1055) rotate(-90)" fill="${CREAM}" text-anchor="middle">
  <text x="0" y="0" font-size="43">${sc(`${cards} Karten`, 43)}</text>
  <text x="0" y="52" font-size="43">${sc(fam, 43)}</text>
</g>
<!-- Seitenwand unten: kleines Logo, CE, Herstellerangaben -->
${logo(380, 1342, 232)}
<path d="${CE}" fill="none" stroke="${CREAM}" stroke-width="9"/>
<g fill="${CREAM}" font-size="18.5">
  <text x="845" y="1372">Hersteller BandeleArt, Riemlistrasse 16, 8187 Weiach, Schweiz</text>
  <text x="845" y="1394">Bestellungen bei: info@bandeleart.ch</text>
  <text x="845" y="1416">Art.-Nr.: ${esc(artNr)} - Stammbaum MeMo</text>
  <text x="845" y="1438">Bitte Verpackung und Angaben aufbewahren</text>
</g>
</svg>`;
}

// SVG-Text → PNG (Blob) in der gewünschten Breite
export function svgToPng(svg, width = 4000) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = width;
      c.height = Math.round(width * BOX_H / BOX_W);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(resolve, 'image/png');
    };
    img.onerror = reject;
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  });
}

// Datei (z.B. die Schrift) als data:-Adresse laden
export async function fileToDataUrl(url) {
  const blob = await (await fetch(url)).blob();
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.readAsDataURL(blob);
  });
}
